/**
 * Streaming helpers: split markdown into top-level blocks (so finished blocks can be memoized and only
 * the last one re-renders), and repair the unfinished tail of a stream.
 */
import { fromMarkdown } from 'mdast-util-from-markdown';
import remend from 'remend';

/**
 * Top-level blocks as source strings, using the parser's node offsets (robust for lists, tables and
 * fenced code that contain blank lines). Concatenating the result gives back `text`.
 */
export function splitBlocks(text) {
  const src = String(text || '');
  if (!src.trim()) return [];
  let tree;
  try {
    tree = fromMarkdown(src);
  } catch (e) {
    return [src];
  }
  const starts = [];
  (tree.children || []).forEach((node) => {
    const offset = node.position && node.position.start ? node.position.start.offset : null;
    if (typeof offset === 'number' && (starts.length === 0 || offset > starts[starts.length - 1])) {
      starts.push(offset);
    }
  });
  if (starts.length === 0) return [src];
  starts[0] = 0; // leading whitespace belongs to the first block
  return starts.map((start, i) => src.slice(start, i + 1 < starts.length ? starts[i + 1] : src.length));
}

// A citation marker cut off at the end of the stream: "[", "[1", "[1,", "[1, 3", "[1、".
const PARTIAL_MARKER_RE = /\[(?:\d{1,2}(?:\s*[,、]\s*\d{0,2})*)?$/;

/** Remove a half-received citation marker from the end of streamed text. */
export function stripPartialMarker(text) {
  return String(text || '').replace(PARTIAL_MARKER_RE, '');
}

/** True when `block` is a table whose delimiter row has not arrived yet (render a skeleton). */
function tableCells(line) {
  let s = String(line || '').trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  const cells = [];
  let cell = '';
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] === '|' && s[i - 1] !== '\\') {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += s[i];
    }
  }
  cells.push(cell.trim());
  return cells;
}

export function isIncompleteTable(block) {
  const trimmed = String(block || '').trim();
  if (!trimmed.startsWith('|')) return false;
  const lines = trimmed.split('\n');
  if (lines.length < 2) return true;
  const header = tableCells(lines[0]);
  const delimiter = tableCells(lines[1]);
  // GFM: the delimiter row needs one `:?-+:?` cell per header cell, otherwise it is not a table yet
  return delimiter.length !== header.length || !delimiter.every((c) => /^:?-+:?$/.test(c));
}

/** Close unterminated emphasis, inline code, links etc. in the streaming tail. */
export function repairTail(block) {
  try {
    return remend(block, { linkMode: 'text-only', images: true, inlineKatex: false, katex: false });
  } catch (e) {
    return block;
  }
}

/**
 * Blocks to render for a message: [{key, md, skeleton}]. While streaming, the trailing partial marker is
 * stripped and only the last block is repaired; finished blocks keep their exact source string so their
 * memoized render is reused.
 */
export function prepareBlocks(text, { streaming = false } = {}) {
  const source = streaming ? stripPartialMarker(text) : String(text || '');
  const blocks = splitBlocks(source);
  return blocks.map((md, i) => {
    const last = i === blocks.length - 1;
    if (streaming && last) {
      if (isIncompleteTable(md)) return { key: `b${i}`, md, skeleton: 'table' };
      return { key: `b${i}`, md: repairTail(md), skeleton: null };
    }
    return { key: `b${i}`, md, skeleton: null };
  });
}
